/**
 * Workflow & Escalation Zod Schemas (F02)
 *
 * Type-safe schemas for workflow_rules and workflow_executions tables.
 *
 * @module services/schemas/workflowSchemas
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const WorkflowTriggerSourceSchema = z.enum([
  'alert', 'pos_exception', 'inventory_event', 'task', 'case', 'schedule',
]);
export type WorkflowTriggerSource = z.infer<typeof WorkflowTriggerSourceSchema>;

export const WorkflowActionTypeSchema = z.enum([
  'create_task', 'send_notification', 'create_case',
]);
export type WorkflowActionType = z.infer<typeof WorkflowActionTypeSchema>;

export const WorkflowExecutionStatusSchema = z.enum([
  'running', 'completed', 'escalated', 'failed',
]);
export type WorkflowExecutionStatus = z.infer<typeof WorkflowExecutionStatusSchema>;

// ============================================
// JSONB SUB-SCHEMAS
// ============================================

/** Trigger conditions stored in workflow_rules.trigger_conditions */
export const TriggerConditionsSchema = z.object({
  severity: z.array(z.string()).optional(),
  type: z.array(z.string()).optional(),
  exception_type: z.array(z.string()).optional(),
  store_ids: z.union([z.literal('all'), z.array(z.string().uuid())]).optional(),
  min_financial_impact: z.number().optional(),
  repeat_count: z.number().int().optional(),
  window: z.string().optional(),
}).passthrough();
export type TriggerConditions = z.infer<typeof TriggerConditionsSchema>;

/** Single action in the workflow_rules.actions array */
export const WorkflowActionSchema = z.object({
  type: WorkflowActionTypeSchema,
  assign_to_role: z.string().optional(),
  priority: z.string().optional(),
  due_in_hours: z.number().optional(),
  channels: z.array(z.string()).optional(),
  template: z.string().optional(),
  conditions: z.record(z.string(), z.unknown()).optional(),
}).passthrough();
export type WorkflowAction = z.infer<typeof WorkflowActionSchema>;

/** Single escalation rule in workflow_rules.escalation_rules */
export const EscalationRuleSchema = z.object({
  if_not_acknowledged_in_hours: z.number(),
  escalate_to_role: z.string(),
  channels: z.array(z.string()).optional(),
}).passthrough();
export type EscalationRule = z.infer<typeof EscalationRuleSchema>;

/** Escalation state stored in workflow_executions.escalation_state */
export const EscalationStateSchema = z.object({
  acknowledged: z.boolean().optional(),
  acknowledged_at: z.string().optional(),
  acknowledged_by: z.string().uuid().optional(),
  escalation_level: z.number().int().optional(),
  last_escalated_at: z.string().optional(),
}).passthrough();
export type EscalationState = z.infer<typeof EscalationStateSchema>;

/** Single executed action logged in workflow_executions.actions_executed */
export const ExecutedActionSchema = z.object({
  type: WorkflowActionTypeSchema,
  result: z.enum(['success', 'failed']),
  entity_id: z.string().uuid().optional(),
  executed_at: z.string(),
  error: z.string().optional(),
}).passthrough();
export type ExecutedAction = z.infer<typeof ExecutedActionSchema>;

// ============================================
// DB ROW SCHEMAS
// ============================================

export const WorkflowRuleDbSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  trigger_source: WorkflowTriggerSourceSchema,
  trigger_conditions: z.any(),
  actions: z.any(),
  escalation_rules: z.any(),
  is_active: z.boolean(),
  priority_order: z.number().int(),
  created_at: z.string(),
});
export type WorkflowRuleRow = z.infer<typeof WorkflowRuleDbSchema>;

export const WorkflowExecutionDbSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  rule_id: z.string().uuid(),
  trigger_entity_type: z.string(),
  trigger_entity_id: z.string().uuid(),
  actions_executed: z.any(),
  escalation_state: z.any(),
  status: WorkflowExecutionStatusSchema,
  started_at: z.string(),
  completed_at: z.string().nullable(),
});
export type WorkflowExecutionRow = z.infer<typeof WorkflowExecutionDbSchema>;

// ============================================
// INPUT SCHEMAS
// ============================================

export const WorkflowRuleInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  trigger_source: WorkflowTriggerSourceSchema,
  trigger_conditions: TriggerConditionsSchema,
  actions: z.array(WorkflowActionSchema).min(1),
  escalation_rules: z.array(EscalationRuleSchema).default([]),
  is_active: z.boolean().default(true),
  priority_order: z.number().int().default(0),
});
export type WorkflowRuleInput = z.infer<typeof WorkflowRuleInputSchema>;

export const WorkflowExecutionInputSchema = z.object({
  rule_id: z.string().uuid(),
  trigger_entity_type: z.string().min(1),
  trigger_entity_id: z.string().uuid(),
  actions_executed: z.array(ExecutedActionSchema).default([]),
  escalation_state: EscalationStateSchema.default({}),
  status: WorkflowExecutionStatusSchema.default('running'),
});
export type WorkflowExecutionInput = z.infer<typeof WorkflowExecutionInputSchema>;

export const WorkflowExecutionFiltersSchema = z.object({
  ruleId: z.string().uuid().optional(),
  status: WorkflowExecutionStatusSchema.optional(),
  triggerEntityType: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});
export type WorkflowExecutionFilters = z.infer<typeof WorkflowExecutionFiltersSchema>;

// ============================================
// OUTPUT / VIEW TYPES
// ============================================

export interface WorkflowExecutionPage {
  data: WorkflowExecutionRow[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================
// TRIGGER SOURCE LABELS (Spanish)
// ============================================

export const TRIGGER_SOURCE_LABELS: Record<WorkflowTriggerSource, string> = {
  alert: 'Alerta',
  pos_exception: 'Excepción POS',
  inventory_event: 'Evento Inventario',
  task: 'Tarea',
  case: 'Caso',
  schedule: 'Programado',
};

export const ACTION_TYPE_LABELS: Record<WorkflowActionType, string> = {
  create_task: 'Crear Tarea',
  send_notification: 'Enviar Notificación',
  create_case: 'Crear Caso',
};

export const EXECUTION_STATUS_LABELS: Record<WorkflowExecutionStatus, string> = {
  running: 'En Ejecución',
  completed: 'Completado',
  escalated: 'Escalado',
  failed: 'Fallido',
};
